import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import { EditPriceRequest, CarCalendarPrice } from '../api/carCalendarApi';
import { useToast } from '@/components/ui/toast/Toast';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Checkbox from '@/components/form/input/Checkbox';
import TextArea from '@/components/form/input/TextArea';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PriceInputWithHints } from './PriceInputWithHints';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EditPriceRequest) => Promise<void>;
  carId: string;
  selectedDate?: Date;
  defaultPrice: number;
  existingPrice: CarCalendarPrice | null;
  isDateRangeMode?: boolean;
}

interface FormErrors {
  price_default?: string;
  discount?: string;
  dateRange?: string;
}

export const CarPriceEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  carId,
  selectedDate,
  defaultPrice = 0,
  existingPrice,
  isDateRangeMode = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [discountDisplay, setDiscountDisplay] = useState('');
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: selectedDate || null,
    to: selectedDate || null,
  });
  const [formData, setFormData] = useState({
    price_default: defaultPrice,
    discount: 0,
    is_promotion: false,
    description: '',
  });

  const [priceDisplay, setPriceDisplay] = useState(defaultPrice.toString());

  const resetForm = () => {
    setFormData({
      price_default: defaultPrice,
      discount: 0,
      is_promotion: false,
      description: '',
    });
    setPriceDisplay(defaultPrice.toString());
    setDiscountDisplay('');
    setErrors({});
    setDateRange({ from: selectedDate || null, to: selectedDate || null });
  };

  useEffect(() => {
    if (existingPrice) {
      const newFormData = {
        price_default: parseFloat(existingPrice.price_default),
        discount: parseFloat(existingPrice.discount),
        is_promotion: existingPrice.is_promotion,
        description: existingPrice.description || '',
      };
      setFormData(newFormData);
      setPriceDisplay(existingPrice.price_default);
      setDiscountDisplay(existingPrice.discount);
    } else {
      setFormData({
        price_default: defaultPrice,
        discount: 0,
        is_promotion: false,
        description: '',
      });
      setPriceDisplay(defaultPrice.toString());
      setDiscountDisplay('');
    }
  }, [existingPrice, defaultPrice]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const price = priceDisplay.trim() === '' ? defaultPrice : parseFloat(priceDisplay);
    if (price < 0) {
      newErrors.price_default = t('cars.price_edit.errors.price_positive');
    }
    if (formData.discount < 0 || formData.discount > 100) {
      newErrors.discount = t('cars.price_edit.errors.discount_range');
    }
    if (isDateRangeMode && (!dateRange.from || !dateRange.to)) {
      newErrors.dateRange = t('cars.price_edit.errors.select_both_dates');
    }
    if (isDateRangeMode && dateRange.from && dateRange.to && dateRange.from > dateRange.to) {
      newErrors.dateRange = t('cars.price_edit.errors.start_before_end');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePriceChange = (value: string) => {
    setPriceDisplay(value);

    const numericValue = value.trim() === '' ? defaultPrice : parseFloat(value);
    setFormData(prev => ({ ...prev, price_default: numericValue }));

    if (numericValue > 0) {
      clearFieldError('price_default');
    }
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscountDisplay(value);

    const numericValue = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({ ...prev, discount: numericValue }));

    if (value === '' || (numericValue >= 0 && numericValue <= 100)) {
      clearFieldError('discount');
    } else {
      setErrors(prev => ({ ...prev, discount: t('cars.price_edit.errors.discount_range') }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const request: EditPriceRequest = {
        car: carId,
        from_dt: isDateRangeMode && dateRange.from
          ? dateRange.from.toLocaleDateString('en-CA')
          : selectedDate!.toLocaleDateString('en-CA'),
        till_dt: isDateRangeMode && dateRange.to
          ? dateRange.to.toLocaleDateString('en-CA')
          : selectedDate!.toLocaleDateString('en-CA'),
        ...formData,
        price_default: priceDisplay.trim() === '' ? defaultPrice : formData.price_default,
      };

      await onSubmit(request);
      resetForm();
      onClose();
      toast.success(t('cars.price_edit.save_success'));
    } catch (error) {
      toast.error(t('cars.price_edit.save_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const setUntilMonthEnd = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setDateRange({ from: today, to: lastDay });
  };

  const handleDateChangeRaw = (value: string) => {
    const [day, month, year] = value.split('.');
    if (day && month && year) {
      const date = new Date(`${year}-${month}-${day}`);
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date;
      }
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {
      resetForm();
      onClose();
    }} className="max-w-[500px] p-6">
      <style>
        {`
          .react-datepicker-wrapper {
            width: 100%;
          }

          .react-datepicker-popper {
            z-index: 20;
          }

          .react-datepicker__month-container {
            float: none !important;
            min-height: 310px !important;
          }

          /* Стили для мобильных устройств */
          @media (max-width: 767px) {
            .react-datepicker-popper {
              position: fixed !important;
              top: auto !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              transform: none !important;
              margin: 1rem !important;
              width: calc(100% - 2rem) !important;
            }

            .react-datepicker {
              width: 100% !important;
            }

            .react-datepicker__month-container {
              width: 100% !important;
              float: none !important;
            }

            .react-datepicker__day {
              width: calc((100% - 2.8rem) / 7) !important;
              line-height: 2.5rem !important;
              margin: 0.2rem !important;
            }

            .react-datepicker__day-name {
              width: calc((100% - 2.8rem) / 7) !important;
              margin: 0.2rem !important;
            }
          }

          /* Стили для десктопа */
          @media (min-width: 768px) {
            .react-datepicker-wrapper {
              position: relative;
            }

            .react-datepicker-popper {
              z-index: 20;
              margin-top: 0.1rem !important;
            }

            .react-datepicker-popper[data-placement^='bottom-start'] {
              margin-left: 0rem !important;
            }

            .react-datepicker-popper[data-placement^='bottom-end'] {
              margin-right: 0.5rem !important;
            }
          }

          .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
            display: none;
          }

          .react-datepicker {
            font-family: inherit;
            border: 1px solid #e5e7eb !important;
            border-radius: 1rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            font-size: 0.875rem;
          }

          .react-datepicker__header {
            background-color: transparent;
            border-bottom: 1px solid #e5e7eb;
            border-radius: 1rem 1rem 0 0;
            padding: 0.75rem 0;
          }

          .react-datepicker__current-month {
            font-weight: 600;
            font-size: 0.875rem;
            color: #1f2937;
            margin-bottom: 0.5rem;
          }

          .react-datepicker__day-name {
            color: #6b7280;
            font-weight: 500;
            font-size: 0.75rem;
            width: 2rem;
            margin: 0.2rem;
          }

          .react-datepicker__day {
            color: #1f2937;
            border-radius: 0.5rem;
            margin: 0.2rem;
            width: 2rem;
            line-height: 2rem;
            font-size: 0.875rem;
          }

          .react-datepicker__day:hover {
            background-color: #f3f4f6;
            border-radius: 0.5rem;
          }

          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected {
            background-color: #3b82f6 !important;
            border-radius: 0.5rem;
            color: white !important;
          }

          .react-datepicker__day--disabled {
            color: #9ca3af;
          }

          .react-datepicker__navigation {
            top: 0.75rem;
          }

          .react-datepicker__navigation-icon::before {
            border-color: #6b7280;
            border-width: 2px 2px 0 0;
            height: 8px;
            width: 8px;
          }

          .dark .react-datepicker {
            background-color: #1e293b;
            border-color: #334155 !important;
          }

          .dark .react-datepicker__header {
            background-color: #1e293b;
            border-color: #334155;
          }

          .dark .react-datepicker__current-month {
            color: #e5e7eb;
          }

          .dark .react-datepicker__day-name {
            color: #9ca3af;
          }

          .dark .react-datepicker__day {
            color: #e5e7eb;
          }

          .dark .react-datepicker__day:hover {
            background-color: #334155;
          }

          .dark .react-datepicker__day--disabled {
            color: #4b5563;
          }

          .dark .react-datepicker__navigation-icon::before {
            border-color: #9ca3af;
          }
        `}
      </style>
      <div>
        <h2 className="text-xl font-semibold mb-4 text-text-primary">
          {existingPrice ? t('cars.price_edit.title.edit') : t('cars.price_edit.title.add')} {t('cars.price_edit.title.price')} {isDateRangeMode ? t('cars.price_edit.title.for_date_range') : t('cars.price_edit.title.for_date', { date: selectedDate?.toLocaleDateString() })}
        </h2>

        <div className="space-y-4">
          {isDateRangeMode && (
            <div className="mb-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="fromDate">{t('cars.price_edit.fields.from_date')}</Label>
                  <ReactDatePicker
                    id="fromDate"
                    selected={dateRange.from}
                    onChange={(date: Date | null) => setDateRange(prev => ({ ...prev, from: date }))}
                    maxDate={dateRange.to || undefined}
                    placeholderText={t('cars.price_edit.placeholders.select_start_date')}
                    className="w-full rounded-lg border border-border-input bg-bg-input py-3 px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-bg-tertiary"
                    dateFormat="dd.MM.yyyy"
                    popperClassName="from-date"
                    popperPlacement="bottom-start"
                    calendarStartDay={1}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setDateRange(prev => ({ ...prev, from: date }));
                        }
                      }
                    }}
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="toDate">{t('cars.price_edit.fields.to_date')}</Label>
                  <ReactDatePicker
                    id="toDate"
                    selected={dateRange.to}
                    onChange={(date: Date | null) => setDateRange(prev => ({ ...prev, to: date }))}
                    minDate={dateRange.from || new Date()}
                    maxDate={undefined}
                    placeholderText={t('cars.price_edit.placeholders.select_end_date')}
                    className="w-full rounded-lg border border-border-input bg-bg-input py-3 px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-bg-tertiary"
                    dateFormat="dd.MM.yyyy"
                    popperClassName="to-date"
                    popperPlacement="bottom-end"
                    calendarStartDay={1}
                    onChangeRaw={(e: any) => {
                      if (e?.target?.value) {
                        const date = handleDateChangeRaw(e.target.value);
                        if (date) {
                          setDateRange(prev => ({ ...prev, to: date }));
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex pt-4">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={setUntilMonthEnd}
                  className="w-full md:w-auto whitespace-nowrap"
                >
                  {t('cars.price_edit.buttons.set_until_month_end')}
                </Button>
              </div>
              {errors.dateRange && <p className="text-danger mt-1">{errors.dateRange}</p>}
            </div>
          )}

          <div>
            <Label>{t('cars.price_edit.fields.price')}</Label>
            <PriceInputWithHints
              value={priceDisplay}
              onChange={handlePriceChange}
              error={errors.price_default}
              carId={carId}
              selectedDate={isDateRangeMode && dateRange.from ? dateRange.from : selectedDate}
              placeholder={t('cars.price_edit.placeholders.enter_price')}
            />
          </div>

          <div>
            <Label>{t('cars.price_edit.fields.discount')}</Label>
            <Input
              type="number"
              step={0.01}
              value={discountDisplay}
              onChange={handleDiscountChange}
              placeholder={t('cars.price_edit.placeholders.enter_discount')}
              error={errors.discount}
            />
          </div>

          <div className="relative top-[5px]">
            <Checkbox
              label={t('cars.price_edit.fields.is_promotion')}
              checked={formData.is_promotion}
              onChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_promotion: checked }))}
            />
          </div>

          <div>
            <Label>{t('cars.price_edit.fields.description')}</Label>
            <TextArea
              value={formData.description}
              onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
              placeholder={t('cars.price_edit.placeholders.enter_description')}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button
              onClick={onClose}
              variant="outline"
              disabled={isSubmitting}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || Object.keys(errors).length > 0}
            >
              {existingPrice ? t('cars.price_edit.buttons.update') : t('cars.price_edit.buttons.save')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
